import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import CenteredCardLayout from '@/components/Organisms/CenteredCardLayout';
import { permissionToUrl } from '@/constants';
import usePermission from '@/hooks/usePermission';
import { useAuthStore } from '@/store';
import FirstLoginChangePasswordForm from './FirstLoginChangePasswordForm';
import LoginForm from './LoginForm';
import LoginLoading from './LoginLoading';
import TwoFactorEmailForm from './TwoFactorEmailForm';
import TwoFactorOTPForm from './TwoFactorOTPForm';
import UserAgreementForm from './UserAgreementForm';

const FIRST_LOGIN_PASSWORD_STEP = 5;
const FIRST_LOGIN_EULA_STEP = 6;

const Login = () => {
	const [loginStep, setLoginStep] = useState(0);
	const [twoFactorEmail, setTwoFactorEmail] = useState('');
	const [prefilledEmail, setPrefilledEmail] = useState('');
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const hasShownResetSuccessRef = useRef(false);
	const isLoggingOut = searchParams.get('logout') === 'true';
	const hasPasswordResetSuccess = searchParams.get('reset') === 'success';
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const user = useAuthStore((state) => state.user);
	const { hasPermission } = usePermission();
	const logout = useAuthStore((state) => state.logout);

	useEffect(() => {
		if (isLoggingOut) {
			logout();
		}
	}, [isLoggingOut, logout]);

	useEffect(() => {
		if (!hasPasswordResetSuccess || hasShownResetSuccessRef.current) {
			return;
		}

		hasShownResetSuccessRef.current = true;
		toast.success(
			'Password updated successfully. Please log in with your new password.',
		);
		navigate('/login', { replace: true });
	}, [hasPasswordResetSuccess, navigate]);

	useEffect(() => {
		if (
			isAuthenticated &&
			user?.userInformation?.isFirstLogin &&
			loginStep === 0 &&
			!isLoggingOut
		) {
			setLoginStep(FIRST_LOGIN_PASSWORD_STEP);
		}
	}, [
		isAuthenticated,
		user?.userInformation?.isFirstLogin,
		loginStep,
		isLoggingOut,
	]);

	if (
		isAuthenticated &&
		loginStep === 0 &&
		!isLoggingOut &&
		!user?.userInformation?.isFirstLogin
	) {
		const firstMatchedRouteAsPerPermission = Object.entries(permissionToUrl).find(
			([permission]) => {
				return hasPermission([permission]);
			},
		);

		return (
			<Navigate
				to={firstMatchedRouteAsPerPermission?.at(1) || '/profile'}
				replace
			/>
		);
	}

	return (
		<CenteredCardLayout
			childrenStyle={
				loginStep === FIRST_LOGIN_EULA_STEP
					? {
							p: { base: 2, md: 4 },
							w: { base: '80%', sm: '70%', md: '65%' },
						}
					: {}
			}
		>
			{isLoggingOut ? (
				<LoginLoading text="Logging you out!" isLoggingOut={isLoggingOut} />
			) : (
				<>
					{loginStep === 0 && (
						<LoginForm
							setLoginStep={setLoginStep}
							setPrefilledEmail={setPrefilledEmail}
						/>
					)}
					{loginStep === 1 && (
						<LoginLoading text="Please hold on a moment as we prepare and set up your working environment." />
					)}
					{loginStep === 2 && (
						<TwoFactorEmailForm
							prefilledEmail={prefilledEmail}
							onSuccess={(email) => {
								setTwoFactorEmail(email);
								setLoginStep(3);
								setTimeout(() => {
									setLoginStep(4);
								}, 1000);
							}}
						/>
					)}
					{loginStep === 3 && (
						<LoginLoading text="Sending OTP to your email..." />
					)}
					{loginStep === 4 && twoFactorEmail && (
						<TwoFactorOTPForm
							email={twoFactorEmail}
							onSuccess={(isFirstLogin) => {
								if (isFirstLogin) {
									setLoginStep(FIRST_LOGIN_PASSWORD_STEP);
								} else {
									navigate('/choose-platform', { replace: true });
								}
							}}
						/>
					)}
					{loginStep === FIRST_LOGIN_PASSWORD_STEP && (
						<FirstLoginChangePasswordForm
							onContinue={() => {
								setLoginStep(FIRST_LOGIN_EULA_STEP);
							}}
						/>
					)}
					{loginStep === FIRST_LOGIN_EULA_STEP && (
						<UserAgreementForm
							onContinue={() => {
								navigate('/choose-platform', { replace: true });
							}}
						/>
					)}
				</>
			)}
		</CenteredCardLayout>
	);
};

export default Login;
